// v2.4.0 — DeliveryPage : router intelligent entre Mapbox Standard 3D (primaire)
// et Leaflet (fallback). Parse les query params, gère la géoloc HTML5 du livreur,
// calcule la distance Haversine, et choisit le renderer selon la présence d'un
// token Mapbox valide.

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import DeliveryMapLeaflet from "./DeliveryMapLeaflet";
import DeliveryMapMapbox from "./DeliveryMapMapbox";
import { toast } from "sonner";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Diagnostic inconditionnel au chargement du module (avant tout rendu React).
// eslint-disable-next-line no-console
console.info(
  "[delivery-diag] token raw:", JSON.stringify(MAPBOX_TOKEN),
  "typeof:", typeof MAPBOX_TOKEN,
  "len:", MAPBOX_TOKEN ? MAPBOX_TOKEN.length : 0,
  "startsWithPk:", typeof MAPBOX_TOKEN === "string" && MAPBOX_TOKEN.startsWith("pk."),
  "hasTrailingSpace:", /\s$/.test(MAPBOX_TOKEN || ""),
);

function isValidMapboxToken(t) {
  // Sanity check : token public Mapbox commence toujours par "pk.".
  return typeof t === "string" && /^pk\./.test(t) && t.length > 20;
}

function haversine(a, b, c, d) {
  const R = 6371000;
  const dLat = ((c - a) * Math.PI) / 180;
  const dLon = ((d - b) * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos((a * Math.PI) / 180) * Math.cos((c * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x)));
}

// v2.15.1 — Bbox Côte d'Ivoire nationale (lat ∈ [4.3, 10.7], lng ∈ [-8.6, -2.4]).
// Si user en-dehors → force origin = Plateau-Abidjan (centroïde standard).
const CI_BBOX = { latMin: 4.3, latMax: 10.7, lngMin: -8.6, lngMax: -2.4 };
const PLATEAU_FALLBACK = [5.3266, -4.0173];
function isInCI(lat, lng) {
  return Number.isFinite(lat) && Number.isFinite(lng)
    && lat >= CI_BBOX.latMin && lat <= CI_BBOX.latMax
    && lng >= CI_BBOX.lngMin && lng <= CI_BBOX.lngMax;
}

export default function DeliveryPage() {
  const [dest, setDest] = useState(null);
  const [destCode, setDestCode] = useState("");
  const [destLabel, setDestLabel] = useState("");
  const [pos, setPos] = useState(null);
  const [outOfCI, setOutOfCI] = useState(false);
  const [distance, setDistance] = useState(null);
  const [arrived, setArrived] = useState(false);
  const [forceLeaflet, setForceLeaflet] = useState(false);
  const watchIdRef = useRef(null);

  // Parse query params (?dest=lat,lng&code=&label=)
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const d = sp.get("dest");
      const code = sp.get("code");
      const label = sp.get("label");
      if (d) {
        const parts = d.split(",").map(parseFloat);
        if (parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
          setDest(parts);
        }
      } else {
        setDest([5.3470, -4.0850]);
      }
      if (code) setDestCode(code);
      if (label) setDestLabel(label);
    } catch (_) {
      setDest([5.3470, -4.0850]);
    }
  }, []);

  // GPS livreur
  // v2.15.1 — Si hors-CI, force origin = Plateau et émet un banner top.
  // v3.6.5 — Param `from=lat,lng` (simulation démo) : position fixe départ boutique.
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const f = sp.get("from");
      if (f) {
        const pp = f.split(",").map((x) => parseFloat(x.trim()));
        if (pp.length === 2 && pp.every(Number.isFinite)) { setPos(pp); return undefined; }
      }
    } catch (_) { /* noop */ }
    if (!navigator.geolocation) {
      setPos(PLATEAU_FALLBACK);
      setOutOfCI(true);
      return;
    }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (p) => {
        const lat = p.coords.latitude;
        const lng = p.coords.longitude;
        if (isInCI(lat, lng)) {
          setPos([lat, lng]);
          setOutOfCI(false);
        } else {
          setPos(PLATEAU_FALLBACK);
          setOutOfCI(true);
        }
      },
      (err) => {
        console.warn("[delivery] geo error:", err.message);
        setPos(PLATEAU_FALLBACK);
        setOutOfCI(true);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 1000 },
    );
    return () => {
      if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
    };
  }, []);

  // Distance refresh 3s
  const recompute = useCallback(() => {
    if (!pos || !dest) return;
    const d = haversine(pos[0], pos[1], dest[0], dest[1]);
    setDistance(d);
    setArrived(d < 30);
  }, [pos, dest]);

  useEffect(() => {
    recompute();
    const id = setInterval(recompute, 3000);
    return () => clearInterval(id);
  }, [recompute]);

  const onTokenInvalid = useCallback((reason) => {
    console.error("[delivery] mapbox token invalid:", reason);
    toast.error("Carte 3D indisponible, mode classique activé");
    setForceLeaflet(true);
  }, []);

  // Décision renderer.
  const useMapbox = !forceLeaflet && isValidMapboxToken(MAPBOX_TOKEN);

  // Log diagnostic pour debug le choix du renderer.
  useEffect(() => {
    const tokenLen = MAPBOX_TOKEN ? MAPBOX_TOKEN.length : 0;
    const valid = isValidMapboxToken(MAPBOX_TOKEN);
    // eslint-disable-next-line no-console
    console.log("[delivery] Mapbox token present:", !!MAPBOX_TOKEN, "valid:", valid, "len:", tokenLen, "useMapbox:", useMapbox);
    if (!useMapbox) {
      // eslint-disable-next-line no-console
      console.warn("[delivery] Mapbox token absent or invalid — falling back to Leaflet");
    }
  }, [useMapbox]);

  const sharedProps = { dest, destCode, destLabel, pos, distance, arrived, outOfCI };
  // v2.18.0b — Les routes PRO (/livreur/*) cachent le bandeau "hors CI" pour
  // permettre le testing diaspora sans pollution UI. /navigate (public) inchangé.
  const _loc = useLocation();
  if (_loc && typeof _loc.pathname === "string" && _loc.pathname.startsWith("/livreur/")) {
    sharedProps.outOfCI = false;
  }

  if (useMapbox && dest) {
    return <DeliveryMapMapbox {...sharedProps} token={MAPBOX_TOKEN} onTokenInvalid={onTokenInvalid} />;
  }
  return <DeliveryMapLeaflet {...sharedProps} />;
}
